import { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../common/Button';
import { formatDate } from '../../utils/formatters';
import './ContactsPanel.css';

const MAX_REASON_LENGTH = 500;

/**
 * Name + email block shared by the active and archived rows below.
 */
const ContactIdentity = ({ profile }) => (
  <>
    <div className="contacts-panel__name">
      {profile.user?.first_name || profile.user?.last_name
        ? `${profile.user.first_name} ${profile.user.last_name}`.trim()
        : <em className="contacts-panel__muted">No name</em>}
    </div>
    <div className="contacts-panel__email">{profile.user?.email}</div>
  </>
);

ContactIdentity.propTypes = {
  profile: PropTypes.object.isRequired,
};

/**
 * ContactsPanel — lists a company's active team members with an Archive
 * action, plus a collapsed "Show archived" section for unarchiving.
 *
 * Archiving blocks the person's login and drops them out of the active
 * list; it never touches their orders or the company link. An archived
 * user shows up nowhere in the active list — only inside the explicitly
 * opened archived section.
 */
const ContactsPanel = ({
  users,
  archivedUsers,
  archivedLoaded,
  onArchive,
  onUnarchive,
  onLoadArchived,
  isSaving,
}) => {
  const [confirmArchiveId, setConfirmArchiveId] = useState(null);
  const [reason, setReason] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);
  // Shown inline in addition to the page-level Alert (from useCustomers'
  // hook-level error state) that other mutations on this page rely on
  // alone — the last-active-user guard-rail message is easy to miss in a
  // banner that can scroll out of view, so it's worth the duplication here.
  const [actionError, setActionError] = useState('');

  const startArchive = (profileId) => {
    setConfirmArchiveId(profileId);
    setReason('');
    setActionError('');
  };

  const cancelArchive = () => {
    setConfirmArchiveId(null);
    setReason('');
    setActionError('');
  };

  const confirmArchive = async (profileId) => {
    try {
      await onArchive(profileId, reason.trim());
      setConfirmArchiveId(null);
      setReason('');
    } catch (err) {
      setActionError(err?.message || 'Failed to archive user.');
    }
  };

  const handleUnarchive = async (profileId) => {
    setActionError('');
    try {
      await onUnarchive(profileId);
    } catch (err) {
      setActionError(err?.message || 'Failed to unarchive user.');
    }
  };

  const toggleArchived = async () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next && !archivedLoaded) {
      setArchivedLoading(true);
      try {
        await onLoadArchived();
      } finally {
        setArchivedLoading(false);
      }
    }
  };

  return (
    <div className="contacts-panel">
      {actionError && <p className="contacts-panel__error">{actionError}</p>}

      {/* ── Active members ───────────────────────────────── */}
      {users.length === 0 ? (
        <p className="contacts-panel__empty">No contacts found.</p>
      ) : (
        <div className="contacts-panel__list">
          {users.map((profile) => (
            <div key={profile.id} className="contacts-panel__row">
              {confirmArchiveId === profile.id ? (
                <div className="contacts-panel__confirm">
                  <span className="contacts-panel__confirm-text">
                    Archive {profile.user?.first_name || profile.user?.email}? They'll
                    lose portal access and drop off this list. Their orders stay intact.
                  </span>
                  <input
                    type="text"
                    className="contacts-panel__reason-input"
                    placeholder="Reason (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
                    disabled={isSaving}
                    autoFocus
                  />
                  <div className="contacts-panel__confirm-actions">
                    <Button type="button" variant="secondary" onClick={cancelArchive} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => confirmArchive(profile.id)}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Archiving...' : 'Archive'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <ContactIdentity profile={profile} />
                  <div className="contacts-panel__meta">
                    <span className="contacts-panel__since">Since {formatDate(profile.created_at)}</span>
                    <button
                      type="button"
                      className="contacts-panel__action contacts-panel__action--archive"
                      onClick={() => startArchive(profile.id)}
                      disabled={isSaving}
                    >
                      Archive
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Archived section (collapsed by default) ─────────── */}
      <button type="button" className="contacts-panel__toggle" onClick={toggleArchived}>
        {showArchived ? 'Hide archived' : 'Show archived'}
        <span className="contacts-panel__toggle-chevron" aria-hidden="true">{showArchived ? '▲' : '▼'}</span>
      </button>

      {showArchived && (
        <div className="contacts-panel__archived">
          {archivedLoading ? (
            <p className="contacts-panel__empty">Loading...</p>
          ) : archivedUsers.length === 0 ? (
            <p className="contacts-panel__empty">No archived users.</p>
          ) : (
            <div className="contacts-panel__list">
              {archivedUsers.map((profile) => (
                <div key={profile.id} className="contacts-panel__row contacts-panel__row--archived">
                  <ContactIdentity profile={profile} />
                  <div className="contacts-panel__meta">
                    <span className="contacts-panel__since">
                      Archived {formatDate(profile.archived_at)}
                      {profile.archived_by_name ? ` by ${profile.archived_by_name}` : ''}
                      {profile.archived_reason ? ` — ${profile.archived_reason}` : ''}
                    </span>
                    <button
                      type="button"
                      className="contacts-panel__action contacts-panel__action--unarchive"
                      onClick={() => handleUnarchive(profile.id)}
                      disabled={isSaving}
                    >
                      Unarchive
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ContactsPanel.propTypes = {
  users: PropTypes.array.isRequired,
  archivedUsers: PropTypes.array.isRequired,
  archivedLoaded: PropTypes.bool.isRequired,
  onArchive: PropTypes.func.isRequired,
  onUnarchive: PropTypes.func.isRequired,
  onLoadArchived: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default ContactsPanel;
